import { createRoot } from "react-dom/client";
import "./index.css";
import { RootErrorBoundary } from "@/components/RootErrorBoundary";
import { DASHBOARD_PATHS } from "@/config/dashboard-paths";
import { isCapacitorNative } from "@/lib/capacitorEnv";

const APP_HOME_HASH = `#${DASHBOARD_PATHS.home}`;

/**
 * Captura erros globais e Promises rejeitadas sem handler. Sem isto, erros assíncronos
 * durante o bootstrap só aparecem na consola — o utilizador vê página em branco.
 */
const BOOT_RELOAD_KEY = "__sf_boot_reloaded_v1";

/** Erros típicos de chunk do build que não baixou (deploy trocou hashes / cache stale / SPA devolveu HTML). */
function isChunkLoadError(input: unknown): boolean {
  const raw =
    typeof input === "string"
      ? input
      : input instanceof Error
        ? input.message
        : (input as { message?: unknown })?.message;
  const msg = (typeof raw === "string" ? raw : "").toLowerCase();
  if (!msg) return false;
  return (
    msg.includes("failed to fetch dynamically imported module") ||
    msg.includes("error loading dynamically imported module") ||
    msg.includes("importing a module script failed") ||
    msg.includes("failed to load module script") ||
    msg.includes("is not a valid javascript mime type") ||
    msg.includes("chunkloaderror") ||
    msg.includes("unexpected token '<'")
  );
}

/** Recarrega no máximo 1x por 30s quando um chunk falha — auto-cura deploy stale sem entrar em loop. */
function reloadOnceForChunkError(reason: string): boolean {
  if (typeof window === "undefined") return false;
  try {
    const now = Date.now();
    let prev = 0;
    try {
      prev = parseInt(sessionStorage.getItem(BOOT_RELOAD_KEY) ?? "0", 10) || 0;
    } catch {
      /* ignore */
    }
    if (now - prev < 30_000) return false;
    try {
      sessionStorage.setItem(BOOT_RELOAD_KEY, String(now));
    } catch {
      /* ignore */
    }
    console.warn("[boot] recarregando por falha de chunk:", reason);
    window.location.reload();
    return true;
  } catch {
    return false;
  }
}

/** Boot OK: remove splash, marca flag e reseta a trava de reload (para o próximo deploy auto-curar). */
function markBooted(): void {
  if (typeof window === "undefined") return;
  try {
    (window as unknown as { __SF_BOOTED__?: boolean }).__SF_BOOTED__ = true;
  } catch {
    /* ignore */
  }
  try {
    sessionStorage.removeItem(BOOT_RELOAD_KEY);
  } catch {
    /* ignore */
  }
  try {
    document.getElementById("sf-boot-splash")?.remove();
  } catch {
    /* ignore */
  }
}

if (typeof window !== "undefined") {
  // Vite dispara este evento quando um módulo importado dinamicamente (chunk) não baixa.
  window.addEventListener("vite:preloadError", (ev) => {
    ev.preventDefault();
    reloadOnceForChunkError("vite:preloadError");
  });

  // Erros de carregamento de recurso (script/link) só são capturáveis na fase de captura.
  window.addEventListener(
    "error",
    (ev) => {
      const target = ev.target as (HTMLElement & { src?: string; href?: string }) | null;
      if (target && (target.tagName === "SCRIPT" || target.tagName === "LINK")) {
        reloadOnceForChunkError(`recurso ${target.tagName} falhou`);
      }
    },
    true,
  );

  window.addEventListener("error", (ev) => {
    if (isChunkLoadError(ev.message) || isChunkLoadError(ev.error)) {
      if (reloadOnceForChunkError(ev.message || "window.error chunk")) return;
    }
    try {
      const payload = {
        kind: "window.error",
        message: ev.message || "(empty)",
        filename: ev.filename || "",
        lineno: ev.lineno ?? -1,
        colno: ev.colno ?? -1,
        errStack: ev.error?.stack?.slice(0, 1200) ?? "(no error)",
      };
      sessionStorage.setItem("__sf_global_error_v1", JSON.stringify(payload));
      console.error("[global error]", payload);
    } catch {
      /* ignore */
    }
  });
  window.addEventListener("unhandledrejection", (ev) => {
    if (isChunkLoadError(ev.reason)) {
      if (reloadOnceForChunkError("unhandledrejection chunk")) return;
    }
    try {
      const reason = ev.reason;
      const payload = {
        kind: "unhandledrejection",
        message: reason instanceof Error ? reason.message : String(reason),
        name: reason instanceof Error ? reason.name : typeof reason,
        stack: reason instanceof Error ? reason.stack?.slice(0, 1200) ?? "" : "",
      };
      sessionStorage.setItem("__sf_unhandled_rejection_v1", JSON.stringify(payload));
      console.error("[unhandledrejection]", payload);
    } catch {
      /* ignore */
    }
  });
}

/**
 * Electron `loadFile` → `file:///.../index.html` sem hash SPA: o BrowserRouter usa o pathname do
 * ficheiro (`/C:/.../index.html`) e cai em 404. Um reload com `#/dashboard` alinha com HashRouter.
 */
function ensureFileUrlHashForSpa(): void {
  if (typeof window === "undefined") return;
  const href = window.location.href;
  if (!href.startsWith("file:")) return;
  const hash = window.location.hash;
  if (hash.startsWith("#/")) return;
  if (!href.includes("index.html")) return;
  window.location.replace(`${href.split("#")[0]}${APP_HOME_HASH}`);
}
ensureFileUrlHashForSpa();

/**
 * Capacitor abre `https://localhost/index.html` — com BrowserRouter isso vira rota
 * inexistente (404). Garantimos entrada `#/dashboard` antes do React montar.
 */
/**
 * WebView Capacitor (bundled) usa `https://localhost/index.html` — não confundir com Vite dev
 * (`http://127.0.0.1:8080`) nem `vite preview`, onde BrowserRouter deve usar o pathname real.
 */
function isCapacitorWebViewRuntime(): boolean {
  if (isCapacitorNative()) return true;
  if (typeof window === "undefined") return false;
  try {
    const { hostname, protocol, pathname } = new URL(window.location.href);
    if (protocol === "capacitor:") return true;
    return hostname === "localhost" && pathname.endsWith("/index.html");
  } catch {
    return false;
  }
}

function ensureCapacitorSpaEntry(): void {
  if (!isCapacitorWebViewRuntime()) return;
  const { pathname, search, hash } = window.location;
  const bareHash = !hash || hash === "#" || hash === "#/";
  if (pathname.endsWith("/index.html") || bareHash) {
    const base = pathname.replace(/\/index\.html$/, "/") || "/";
    window.history.replaceState(null, "", `${base}${search}${APP_HOME_HASH}`);
    return;
  }
  // WebView restaurou #/settings/billing sem retorno Stripe → home = dashboard
  const hashBody = hash.startsWith("#") ? hash.slice(1) : hash;
  const hashPath = hashBody.split("?")[0] || "/";
  const hashQuery = hashBody.includes("?") ? hashBody.slice(hashBody.indexOf("?") + 1) : "";
  const billingParams = new URLSearchParams(hashQuery || search);
  const isStripeBillingReturn =
    hashPath.startsWith("/settings/billing") &&
    (billingParams.has("checkout") || billingParams.has("session_id"));
  if (hashPath.startsWith("/settings/billing") && !isStripeBillingReturn) {
    const base = pathname.replace(/\/index\.html$/, "/") || "/";
    window.history.replaceState(null, "", `${base}${APP_HOME_HASH}`);
    return;
  }
  // Fallback: site_url OAuth legado usava /auth — HashRouter no APK espera #/auth.
  if (pathname === "/auth" && !hash.startsWith("#/")) {
    window.history.replaceState(null, "", `/${search}#/auth`);
  }
}
ensureCapacitorSpaEntry();

/**
 * PWA: registo do service worker (build web apenas; eliminado no bundle Electron).
 *
 * Estratégia de atualização (revista em 2026-05-21c):
 * • O SW usa NetworkFirst para navegação (HTML) — então cada visita já recebe a
 *   versão mais recente sem precisar de force-reload via `controllerchange`.
 * • `registration.update()` periódico (60s) para detectar deploys em sessões
 *   longas (puxa o novo sw.js do servidor; o NetworkFirst no SW já cuida do HTML).
 */
if (!__ELECTRON_BUILD__ && !isCapacitorNative() && typeof window !== "undefined") {
  void import("virtual:pwa-register").then(({ registerSW }) => {
    registerSW({
      immediate: true,
      onRegisteredSW(_swUrl, registration) {
        if (!registration) return;
        const intervalMs = 60_000;
        setInterval(() => {
          registration.update().catch(() => {});
        }, intervalMs);
      },
    });
  });
}

const DISALLOWED_SUPABASE_REF = "yrtshgjzdqeimrblkkih";
const DEPRECATED_CLOUD_SUPABASE_REF = "otnmrsjgzpmynwgialcg";
const ACTIVE_SUPABASE_HOST = "supabase.automvolga.tech";

function getSupabaseBootstrapError(): string | null {
  const url = import.meta.env.VITE_SUPABASE_URL;
  const key = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

  if (!url || !key) {
    return (
      "Faltam variáveis de ambiente.\n\n" +
      "Crie o ficheiro .env na raiz do projeto com:\n" +
      "• VITE_SUPABASE_URL\n" +
      "• VITE_SUPABASE_PUBLISHABLE_KEY\n\n" +
      "(Veja .env.example.) Depois pare e volte a executar npm run dev."
    );
  }

  if (typeof url !== "string" || !url.startsWith("http")) {
    return "VITE_SUPABASE_URL deve ser uma URL HTTPS válida (ex.: https://supabase.automvolga.tech).";
  }

  if (url.includes(DEPRECATED_CLOUD_SUPABASE_REF)) {
    return (
      `VITE_SUPABASE_URL aponta para o projeto cloud removido (${DEPRECATED_CLOUD_SUPABASE_REF}.supabase.co).\n\n` +
      `Use o Supabase self-hosted: https://${ACTIVE_SUPABASE_HOST} e a ANON_KEY da VPS no .env / Vercel.`
    );
  }

  if (url.includes(DISALLOWED_SUPABASE_REF)) {
    return (
      `VITE_SUPABASE_URL aponta para o projeto errado (${DISALLOWED_SUPABASE_REF}).\n\n` +
      `Use https://${ACTIVE_SUPABASE_HOST} e a anon key correta no .env / Vercel.`
    );
  }

  if (typeof key !== "string" || key.length < 32) {
    return "VITE_SUPABASE_PUBLISHABLE_KEY parece incompleta. Copie a anon key completa do painel Supabase (Project Settings → API).";
  }

  return null;
}

function ConfigErrorScreen({ message }: { message: string }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <div className="max-w-lg rounded-xl border border-border bg-card p-6 text-card-foreground shadow-sm">
        <h1 className="text-lg font-semibold">Não foi possível iniciar a aplicação</h1>
        <p className="mt-3 text-sm text-muted-foreground whitespace-pre-wrap">{message}</p>
        <p className="mt-4 text-xs text-muted-foreground">
          Abra também a consola do navegador (F12 → Consola) para ver erros adicionais.
        </p>
      </div>
    </div>
  );
}

const rootEl = document.getElementById("root");

if (!rootEl) {
  document.body.textContent = "Erro: falta o elemento #root no index.html.";
} else {
  const bootstrapError = getSupabaseBootstrapError();

  // #region Dev: mostra sempre o origin real (evita confusão quando a 8080 está ocupada e o Vite sobe na 8081+)
  if (import.meta.env.DEV && typeof document !== "undefined" && !bootstrapError) {
    const bar = document.createElement("aside");
    bar.setAttribute("data-dev-session-origin", "");
    bar.setAttribute("aria-live", "polite");
    bar.style.cssText =
      "position:fixed;bottom:12px;right:12px;z-index:2147483647;font:11px/1.45 system-ui,Segoe UI,sans-serif;" +
      "padding:10px 12px;max-width:min(94vw,400px);word-break:break-word;" +
      "background:rgba(15,23,42,.92);color:#e2e8f0;border-radius:8px;border:1px solid rgba(148,163,184,.25);" +
      "box-shadow:0 4px 24px rgba(0,0,0,.35);pointer-events:none";
    bar.textContent =
      `Sessão dev: ${typeof window !== "undefined" ? window.location.origin : ""} — usa este URL; se abrires ` +
      `outra porta (ex.: processo velho na 8080), vês código antigo. Hard refresh: Ctrl+Shift+R.`;
    document.body.appendChild(bar);
    console.info("[vite dev] página servida desde:", typeof window !== "undefined" ? window.location.href : "");
  }
  // #endregion

  if (bootstrapError) {
    createRoot(rootEl).render(<ConfigErrorScreen message={bootstrapError} />);
    markBooted();
  } else {
    void (async () => {
      try {
        const { default: App } = await import("./App.tsx");
        createRoot(rootEl).render(
          <RootErrorBoundary>
            <App />
          </RootErrorBoundary>
        );
        markBooted();
      } catch (err: unknown) {
        // Falha de chunk após deploy (hashes mudaram / cache stale): tenta 1 reload automático.
        if (isChunkLoadError(err) && reloadOnceForChunkError("import(App.tsx) falhou")) {
          return; // a página vai recarregar
        }
        const message =
          err instanceof Error ? err.message : "Falha ao carregar o módulo principal da aplicação.";
        createRoot(rootEl).render(
          <ConfigErrorScreen message={`${message}\n\nVerifique a consola (F12) para a pilha completa.`} />
        );
        markBooted();
        console.error("[main] Falha ao importar App", err);
      }
    })();
  }
}
